/*
 * Copyright (C) 20019 FlexLink
 * Original CPP Code Copyright (C) 2007-2016 Rafael Durbano Lobato
 *
 * This file is part of Recursive Partitioning Algorithm.
 *
 * Recursive Partitioning Algorithm is free software: you can
 * redistribute it and/or modify it under the terms of the GNU General
 * Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * Recursive Partitioning Algorithm is distributed in the hope that it
 * will be useful, but WITHOUT ANY WARRANTY; without even the implied
 * warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR
 * PURPOSE. See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with Recursive Partitioning Algorithm. If not, see
 * <http://www.gnu.org/licenses/>.
 *
 * Contact information:
 *
 * Rafael Durbano Lobato <lobato@ime.usp.br>
 * http://www.ime.usp.br/~lobato/
 */

/* Implementation of the algorithm "Recursive Five-block Heuristic"
 * described in [1] for solving the Manufacturer's Pallet Loading
 * Problem.
 *
 * [1] E. G. Birgin, R. D. Lobato and R. Morabito. An effective
 *     recursive partitioning approach for the packing of identical
 *     rectangles in a rectangle. Journal of the Operational Research
 *     Society (2010) 61, 306-320.
 *
 * Last update: July 8th, 2009
 * Author:      Rafael Durbano Lobato
 * Contact:     lobato@ime.usp.br
 */

/******************************************************************
 ******************************************************************/

import { ApContext } from './apcontext'
import { newCutPoint } from './util'
import { newSet, constructRasterPoints, constructConicCombinations } from './sets'

const INFINITY_ = 2000000000
const N_INFINITY = false

/* #define lowerBound(L, W, l, w) std::max((L / l) * (W / w), (L / w) * (W / l)) */
function lowerBound (L, W, l, w) {
  return Math.max(Math.floor(L / l) * Math.floor(W / w), Math.floor(L / w) * Math.floor(W / l))
}

/* Maximum level of recursion (maximum tree search depth). */
let N = INFINITY_

/******************************************************************
 ******************************************************************/

// inline int localUpperBound(int iX, int iY)
function localUpperBound (iX, iY) {
  if (ApContext.solutionDepth[iX][iY] === -1) {
    return ApContext.lowerBound[iX][iY]
  } else {
    return ApContext.upperBound[iX][iY]
  }
}

/******************************************************************
 ******************************************************************/

/**
 * Store the points x1, x2, y1 and y2 that determine the cut in the
 * rectangle (L,W).
 */
// void storeCutPoint(int L, int W, int x1, int x2, int y1, int y2) {
function storeCutPoint (L, W, x1, x2, y1, y2) {
  // noinspection UnnecessaryLocalVariableJS
  const c = newCutPoint(x1, x2, y1, y2, 0)
  ApContext.cutPoints[ApContext.indexX[L]][ApContext.indexY[W]] = c
}

/******************************************************************
 ******************************************************************/

/**
 * Auxiliar function.
 *
 * Parameters:
 *
 * L - Length of the pallet.
 * W - Width of the pallet.
 * l - Length of the boxes.
 * w - Width of the boxes.
 * n - Maximum search depth.
 *
 * numBlocks - Number of blocks in the current division of the pallet.
 *
 * L_, W_ - Lenghts and widths for each partition of the pallet.
 *
 * z_lb   - Current lower bound for (L,W).
 * z_ub   - Upper bound for (L,W).
 *
 * x1, x2, y1, y2 - Points that determine the division of the pallet.
 *
 * Return:
 * - the number of (l,w)-boxes packed into (L,W) pallet, using the
 *   division determined by (x1,x2,y1,y2).
 */
// int solve(int L, int W, int l, int w, int n, int numBlocks, int *L_, int *W_,
//           int *z_lb, int z_ub, int x1, int x2, int y1, int y2) {
// JS: z_lb is single-element array to simulate pointer.
export function solve (L, W, l, w, n, numBlocks, L_, W_, z_lb, z_ub, x1, x2, y1, y2) {
  /* z[1..5] stores the amount of boxes packed into partitions 1 to 5. */
  const z = new Array(6).fill(0) // int z[6];

  /* Sum of the lower and upper bounds for the number of boxes that
   * can be packed into partitions 1 to 5. */
  // int S_lb, S_ub;
  let S_lb = 0; let S_ub = 0
  /* Indices of the subproblems in the indexing matrices. */
  const iX = new Array(6).fill(0); const iY = new Array(6).fill(0) // int iX[6], iY[6];

  /* Lower and upper bounds for each partition. */
  // int zi_ub[6], zi_lb[6];
  const zi_ub = new Array(6).fill(0); const zi_lb = new Array(6).fill(0)

  let i

  /* Normalize each rectangle produced. */
  for (i = 1; i <= numBlocks; i++) {
    /* Normalize the size of the rectangle (Li,Wi). */
    L_[i] = ApContext.normalize[L_[i]]
    W_[i] = ApContext.normalize[W_[i]]

    /* We assume that Li >= Wi. */
    if (L_[i] < W_[i]) {
      [L_[i], W_[i]] = [W_[i], L_[i]]
    }

    /* Get the indices of each subproblem in the indexing matrices. */
    iX[i] = ApContext.indexX[L_[i]]
    iY[i] = ApContext.indexY[W_[i]]
  }

  /* If maximum level of the recursion was not reached. */
  if (n < N) {
    /* Store the sum of best packing estimations in the 5 partitions
     * until this moment. Initially, it receives the sum of the lower
     * bounds. */
    S_lb = 0

    /* Sum of the upper bounds in the 5 partitions. */
    S_ub = 0

    /* Compute the lower (zi_lb) and upper (zi_ub) bounds of each
     * partition. */
    for (i = 1; i <= numBlocks; i++) {
      /* Lower bound of (Li, Wi). */
      zi_lb[i] = ApContext.lowerBound[iX[i]][iY[i]]
      S_lb += zi_lb[i]
      /* Upper bound of (Li, Wi). */
      zi_ub[i] = localUpperBound(iX[i], iY[i])
      S_ub += zi_ub[i]
    }

    if (z_lb[0] < S_ub) {
      /* The current lower bound is less than the sum of the partitions
       * upper bounds. Then, there is a possibility of this division
       * improve the solution. */

      for (i = 1; i <= numBlocks; i++) {
        if (N_INFINITY) {
          if (ApContext.solutionDepth[iX[i]][iY[i]] >= 0) {
            z[i] = BD(L_[i], W_[i], l, w, n + 1)
            ApContext.lowerBound[iX[i]][iY[i]] = z[i]
            ApContext.solutionDepth[iX[i]][iY[i]] = -1
          } else {
            /* The problem was already solved. */
            z[i] = ApContext.lowerBound[iX[i]][iY[i]]
          }
        } else {
          if (ApContext.solutionDepth[iX[i]][iY[i]] > n) {
            /* Solve for the first time or give another chance for
             * this problem. */
            z[i] = BD(L_[i], W_[i], l, w, n + 1)
            ApContext.lowerBound[iX[i]][iY[i]] = z[i]

            if (ApContext.reachedLimit[iX[i]][iY[i]] === 0) {
              ApContext.solutionDepth[iX[i]][iY[i]] = -1
            } else {
              ApContext.solutionDepth[iX[i]][iY[i]] = n
            }
          } else {
            /* This problem was already solved. It gets the
             * solution obtained previously. */
            z[i] = ApContext.lowerBound[iX[i]][iY[i]]
          }
        }

        if (ApContext.reachedLimit[iX[i]][iY[i]] === 1) {
          ApContext.reachedLimit[ApContext.indexX[L]][ApContext.indexY[W]] = 1
        }

        /* Update lower and upper bounds for this partitioning. */
        S_lb = S_lb - zi_lb[i] + z[i]
        S_ub = S_ub - zi_ub[i] + z[i]

        /* If z_lb >= S_ub, we have, at least, a solution as good as
         * the one that can be find with this partitioning. So this
         * partitioning is discarded. */
        if (z_lb[0] >= S_ub) {
          break
        }

        /* If the sum of packings in the current partitions is better
         * than the previous, update the lower bound. */
        else if (S_lb > z_lb[0]) {
          z_lb[0] = S_lb
          storeCutPoint(L, W, x1, x2, y1, y2)
          if (z_lb[0] === z_ub) {
            /* An optimal solution was found. */
            ApContext.solutionDepth[ApContext.indexX[L]][ApContext.indexY[W]] = -1
            ApContext.reachedLimit[ApContext.indexX[L]][ApContext.indexY[W]] = 0
            return 1
          }
        }
      }
    }
  } /* if n < N */

  /* The maximum depth of recursion was reached. Then, each partition
   * will not be solved recursivelly. Each one receives the best
   * packing obtained until this moment. */
  else {
    ApContext.reachedLimit[ApContext.indexX[L]][ApContext.indexY[W]] = 1
    S_lb = 0

    /* Compute the lower bound of each partition and the sum is
     * stored in S_lb. */
    for (i = 1; i <= numBlocks; i++) {
      S_lb += ApContext.lowerBound[iX[i]][iY[i]]
    }

    /* If the sum of the homogeneous packing in all current
     * partitions is better than the previous estimation for (L,W),
     * update the lower bound. */
    if (S_lb > z_lb[0]) {
      z_lb[0] = S_lb
      storeCutPoint(L, W, x1, x2, y1, y2)
      if (z_lb[0] === z_ub) {
        /* An optimal solution was found. */
        ApContext.solutionDepth[ApContext.indexX[L]][ApContext.indexY[W]] = -1
        ApContext.reachedLimit[ApContext.indexX[L]][ApContext.indexY[W]] = 0
        return 1
      }
    }
  }
  return 0
}

/******************************************************************
 ******************************************************************/

/**
 * Guillotine and first order non-guillotine cuts recursive procedure.
 *
 * Parameters:
 * L - Length of the pallet.
 * W - Width of the pallet.
 * l - Length of the boxes.
 * w - Width of the boxes.
 * n - Maximum search depth.
 *
 * Return:
 * - the number of (l,w)-boxes packed into (L,W) pallet.
 */
// int BD(int L, int W, int l, int w, int n) {
function BD (L, W, l, w, n) {
  /* Lower and upper bounds for the number of (l,w)-boxes that can be
   * packed into (L,W). */
  // int z_lb, z_ub;
  const z_lb = [null]; let z_ub // JS: use single-element array as z_lb has to be passed by reference.

  /* We assume that L >= W. */
  if (W > L) {
    [L, W] = [W, L]
  }

  z_lb[0] = ApContext.lowerBound[ApContext.indexX[L]][ApContext.indexY[W]]
  z_ub = localUpperBound(ApContext.indexX[L], ApContext.indexY[W])

  if (z_lb[0] === 0 || z_lb[0] === z_ub) {
    /* An optimal solution was found: no box fits into the pallet or
     * lower and upper bounds are equal. */
    ApContext.solutionDepth[ApContext.indexX[L]][ApContext.indexY[W]] = -1
    ApContext.reachedLimit[ApContext.indexX[L]][ApContext.indexY[W]] = 0
    return z_lb[0]
  } else {
    /* Points that determine the pallet division. */
    // int x1, x2, y1, y2;
    let x1, x2, y1, y2

    /* Indices of x1, x2, y1 and y2 in the raster points arrays. */
    // int index_x1, index_x2, index_y1, index_y2;
    let index_x1, index_x2, index_y1, index_y2

    /* Size of the generated partitions:
     * (L_[i], W_[i]) is the size of the partition i, for i = 1, ..., 5. */
    // int L_[6], W_[6];
    const L_ = new Array(6).fill(0); const W_ = new Array(6).fill(0)

    /* Raster points sets X' and Y' for this problem. */
    // Set rasterX, rasterY;
    /* Construct the raster points sets. */
    const { rasterPointsX: rasterX, rasterPointsY: rasterY } = constructRasterPoints(L, W, ApContext.normalSetX)

    // int solved;
    let solved

    ApContext.reachedLimit[ApContext.indexX[L]][ApContext.indexY[W]] = 0

    /*
     * Loop to generate the cut points (x1, x2, y1 and y2) considering
     * the following symmetries.
     *
     * - First order non-guillotine cuts:
     *   0 < x1 < x2 < L and 0 < y1 < y2 < W
     *   (x1 + x2 < L) or (x1 + x2 = L and y1 + y2 <= W)
     *
     * - Vertical guillotine cuts:
     *   0 < x1 = x2 <= L/2 and y1 = y2 = 0
     *
     * - Horizontal guillotine cuts:
     *   0 < y1 = y2 <= W/2 and x1 = x2 = 0
     *
     * Observation: In the loop of non-guillotine cuts it can appear less
     * than five partitions in the case of the normalization of a side
     * of a partition be zero.
     */

    /* #################################*
     * First order non-guillotine cuts *
     *################################# */

    /*
         L_1     L_2
        ----------------
       |     |    2     |W_2
    W_1|  1  |          |
       |     |----------|
       |     | 3 |      |
       |---------|      |
       |         |  5   |W_5
    W_4|    4    |      |
       |         |      |
        ----------------
           L_4     L_5
    */

    for (index_x1 = 1; index_x1 < rasterX.size &&
    rasterX.points[index_x1] <= L / 2; index_x1++) {
      x1 = rasterX.points[index_x1]

      for (index_x2 = index_x1 + 1; index_x2 < rasterX.size &&
      rasterX.points[index_x2] + x1 <= L; index_x2++) {
        x2 = rasterX.points[index_x2]

        for (index_y1 = 1; index_y1 < rasterY.size &&
        rasterY.points[index_y1] < W; index_y1++) {
          y1 = rasterY.points[index_y1]

          for (index_y2 = index_y1 + 1; index_y2 < rasterY.size &&
          rasterY.points[index_y2] < W; index_y2++) {
            y2 = rasterY.points[index_y2]

            /* Symmetry. When x1 + x2 = L, we can restrict y1 and y2
             * to y1 + y2 <= W. */
            if (x1 + x2 === L && y1 + y2 > W) { break }

            /* The five partitions. */
            L_[1] = x1
            W_[1] = W - y1

            L_[2] = L - x1
            W_[2] = W - y2

            L_[3] = x2 - x1
            W_[3] = y2 - y1

            L_[4] = x2
            W_[4] = y1

            L_[5] = L - x2
            W_[5] = y2

            solved = solve(L, W, l, w, n, 5, L_, W_, z_lb, z_ub, x1, x2, y1, y2)

            if (solved) {
              /* This problem was solved with optimality guarantee. */
              return z_lb[0]
            }
          } /* for y2 */
        } /* for y1 */
      } /* for x2 */
    } /* for x1 */

    /* ###########################*
     * Vertical guillotine cuts. *
     *########################### */

    /*
       ----------------
      |     |          |
      |     |          |
      |     |          |
      |  1  |    2     |
      |     |          |
      |     |          |
      |     |          |
      |     |          |
       ----------------
    */

    for (index_x1 = 1; index_x1 < rasterX.size &&
    rasterX.points[index_x1] <= L / 2; index_x1++) {
      x1 = x2 = rasterX.points[index_x1]
      y1 = y2 = 0

      /* Partitions 1 and 2 generated by the vertical cut. */
      L_[1] = x1
      W_[1] = W - y1

      L_[2] = L - x1
      W_[2] = W - y2

      solved = solve(L, W, l, w, n, 2, L_, W_, z_lb, z_ub, x1, x2, y1, y2)
      if (solved) {
        /* This problem was solved with optimality guarantee. */
        return z_lb[0]
      }
    }

    /* #############################*
     * Horizontal guillotine cuts. *
     *############################# */

    /*
       ----------------
      |       2        |
      |                |
      |----------------|
      |                |
      |                |
      |       5        |
      |                |
      |                |
       ----------------
    */

    for (index_y1 = 1; index_y1 < rasterY.size &&
    rasterY.points[index_y1] <= W / 2; index_y1++) {
      y1 = y2 = rasterY.points[index_y1]
      x1 = x2 = 0

      /* Partitions 2 and 5 generated by the horizontal cut. */
      L_[1] = L - x1
      W_[1] = W - y2

      L_[2] = L - x2
      W_[2] = y2

      solved = solve(L, W, l, w, n, 2, L_, W_, z_lb, z_ub, x1, x2, y1, y2)
      if (solved) {
        /* This problem was solved with optimality guarantee. */
        return z_lb[0]
      }
    }

    return z_lb[0]
  }
}

/******************************************************************
 ******************************************************************/

/**
 * Compute the Barnes's upper bound [3].
 *
 * [3] F. W. Barnes. Packing the maximum number of m x n tiles in a
 *     large p x q rectangle. Discrete Mathematics, volume 26,
 *     pages 93-100, 1979.
 *
 * Parameters:
 * (L, W) Pallet dimensions.
 * (l, w) Dimensions of the boxes to be packed.
 *
 * Return:
 * - the computed Barnes's bound.
 */
// int barnesBound(int L, int W, int l, int w)
function barnesBound (L, W, l, w) {
  // int r, s, D;
  // int minWaste = (L * W) % (l * w);
  let r, s, D
  const minWaste = (L * W) % (l * w)

  /* (l,1)-boxes packing. */
  r = L % l
  s = W % l
  // int A = std::min(r * s, (l - r) * (l - s));
  const A = Math.min(r * s, (l - r) * (l - s))

  /* (1,w)-boxes packing. */
  r = L % w
  s = W % w
  // int B = std::min(r * s, (w - r) * (w - s));
  const B = Math.min(r * s, (w - r) * (w - s))

  /* Best unitary tile packing. */
  // int maxAB = std::max(A, B);
  const maxAB = Math.max(A, B)

  if (minWaste >= maxAB % (l * w)) {
    /* Wasted area. */
    D = (maxAB / (l * w)) * (l * w) + minWaste
  } else {
    /* Wasted area. */
    D = Math.floor(maxAB / (l * w) + 1) * (l * w) + minWaste
  }

  return Math.floor((L * W - D) / (l * w))
}

// JS helper
// Makes 2d number array filled with zeros unless valfn is provided.
// With valfm calls valfn for every element.
// N.B. array is [x][y] as mandated by cpp code.
function makeArray2d (x, y, valfn) {
  return new Array(x).fill()
    .map(() => new Array(y).fill(0)
      .map(v => (valfn) ? valfn() : v))
}

/******************************************************************
 ******************************************************************/
// void initialize(int L, int W, int l, int w) {
// JS: exporte for tests
export function initialize (L, W, l, w) {
  /* Normalization of L and W. */
  let L_n, W_n // int L_n, W_n;
  let i, j // int i, j;

  /* Construct the conic combination set of l and w. */
  ApContext.normalSetX = newSet(L + 2)
  constructConicCombinations(L, l, w, ApContext.normalSetX)

  /* Compute the values of L* and W*.
   * normalize[i] = max {x in X | x <= i} */
  // normalize = new int[L + 1];
  ApContext.normalize = new Array(L + 1)
  i = 0
  for (j = 0; j <= L; j++) {
    // noinspection StatementWithEmptyBodyJS - is ok here as it finds i
    for (; i < ApContext.normalSetX.size && ApContext.normalSetX.points[i] <= j; i++) ;
    ApContext.normalize[j] = ApContext.normalSetX.points[i - 1]
  }

  /* Normalize (L, W). */
  L_n = ApContext.normalize[L]
  W_n = ApContext.normalize[W]

  const { rasterPointsX, rasterPointsY } = constructRasterPoints(L, W, ApContext.normalSetX)
  const rasterX = rasterPointsX
  const rasterY = rasterPointsY

  ApContext.normalSetX = newSet(L_n + 2)

  // int k = 0;
  let k = 0
  i = 0
  j = 0
  while (i < rasterX.size && rasterX.points[i] <= L_n &&
  j < rasterY.size && rasterY.points[j] <= W_n) {
    if (rasterX.points[i] === rasterY.points[j]) {
      ApContext.normalSetX.points[k++] = rasterX.points[i++]
      ApContext.normalSetX.size++
      j++
    } else if (rasterX.points[i] < rasterY.points[j]) {
      ApContext.normalSetX.points[k++] = rasterX.points[i++]
      ApContext.normalSetX.size++
    } else {
      ApContext.normalSetX.points[k++] = rasterY.points[j++]
      ApContext.normalSetX.size++
    }
  }
  while (i < rasterX.size && rasterX.points[i] <= L_n) {
    if (rasterX.points[i] > ApContext.normalSetX.points[k - 1]) {
      ApContext.normalSetX.points[k++] = rasterX.points[i]
      ApContext.normalSetX.size++
    }
    i++
  }
  if (k > 0 && ApContext.normalSetX.points[k - 1] < L_n) {
    ApContext.normalSetX.points[k++] = L_n
    ApContext.normalSetX.size++
  }
  ApContext.normalSetX.points[k] = L_n + 1
  ApContext.normalSetX.size++

  /* Construct the array of indices. */
  // indexX = new int[L_n + 2];
  // indexY = new int[W_n + 2];
  ApContext.indexX = new Array(L_n + 2).fill(0)
  ApContext.indexY = new Array(W_n + 2).fill(0)

  for (i = 0; i < ApContext.normalSetX.size; i++) {
    ApContext.indexX[ApContext.normalSetX.points[i]] = i
  }
  // int ySize = 0;
  let ySize = 0
  for (i = 0; i < ApContext.normalSetX.size; i++) {
    if (ApContext.normalSetX.points[i] > W_n) {
      break
    }
    ySize++
    ApContext.indexY[ApContext.normalSetX.points[i]] = i
  }

  // solutionDepth   = new int*[normalSetX.size];
  // upperBound      = new int*[normalSetX.size];
  // lowerBound      = new int*[normalSetX.size];
  // reachedLimit    = new int*[normalSetX.size];
  // cutPoints       = new CutPoint*[normalSetX.size];
  // for (i = 0; i < normalSetX.size; i++) {
  //   solutionDepth[i] = new int[ySize];
  //   upperBound[i] = new int[ySize];
  //   lowerBound[i] = new int[ySize];
  //   cutPoints[i] = new CutPoint[ySize];
  //   reachedLimit[i] = new int[ySize];
  // }
  ApContext.solutionDepth = makeArray2d(ApContext.normalSetX.size, ySize)
  ApContext.upperBound = makeArray2d(ApContext.normalSetX.size, ySize)
  ApContext.lowerBound = makeArray2d(ApContext.normalSetX.size, ySize)
  ApContext.reachedLimit = makeArray2d(ApContext.normalSetX.size, ySize)
  // todo-jr:: probably enough to init with nulls:
  ApContext.cutPoints = makeArray2d(ApContext.normalSetX.size, ySize, () => newCutPoint())

  for (i = 0; i < ApContext.normalSetX.size; i++) {
    const x = ApContext.normalSetX.points[i]

    for (j = 0; j < ySize; j++) {
      const y = ApContext.normalSetX.points[j]

      ApContext.solutionDepth[i][j] = N
      ApContext.reachedLimit[i][j] = 1
      ApContext.upperBound[i][j] = barnesBound(x, y, l, w)
      ApContext.lowerBound[i][j] = lowerBound(x, y, l, w)
      ApContext.cutPoints[i][j].homogeneous = 1
    }
  }
}

/// //////////////////////////////////////////////////////////////

// JS: from bd.h:
/**
 * Guillotine and first order non-guillotine cuts recursive procedure.
 *
 * Parameters:
 * L     - Length of the pallet.
 * W     - Width of the pallet.
 * l     - Length of the boxes.
 * w     - Width of the boxes.
 * N_max - Maximum search depth.
 *
 * Return:
 * - the number of (l,w)-boxes packed into (L,W) pallet.
 *
 * int solve_BD(int L, int W, int l, int w, int N_max)
 */
export function solve_BD (L, W, l, w, N_max) {
  // int L_n, W_n;
  let L_n, W_n

  N = N_max
  if (N <= 0) {
    N = INFINITY_
  }

  /* We assume that L >= W. */
  if (W > L) {
    [L, W] = [W, L]
  }

  initialize(L, W, l, w)

  /* Normalize (L, W). */
  L_n = ApContext.normalize[L]
  W_n = ApContext.normalize[W]

  // int solution = BD(L_n, W_n, l, w, N);
  let solution = BD(L_n, W_n, l, w, N)

  if (solution !== ApContext.upperBound[ApContext.indexX[L_n]][ApContext.indexY[W_n]] && N !== 1) {
    /* If the solution found is not optimal (or, at least, if it is not
     * possible to prove its optimality), solve from the first level. */
    solution = BD(L_n, W_n, l, w, 1)
  }

  ApContext.lowerBound[ApContext.indexX[L_n]][ApContext.indexY[W_n]] = solution

  return solution
}
